import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { User } from 'firebase/auth';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, firestore, shouldBypassAuth } from '../lib/firebase';

type AuthStatus = 'checking' | 'authenticated' | 'unauthenticated' | 'bypassed';

interface AuthContextValue {
  status: AuthStatus;
  user: User | null;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [status, setStatus] = useState<AuthStatus>('checking');
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);

  const bypassAuth = useMemo(() => shouldBypassAuth(), []);

  useEffect(() => {
    if (bypassAuth) {
      setStatus('bypassed');
      setUser(null);
      return;
    }

    if (!auth || !firestore) {
      console.error('Firebase não foi inicializado (config ausente?)');
      setStatus('unauthenticated');
      setError('Configuração do Firebase ausente.');
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setStatus('unauthenticated');
        return;
      }

      setStatus('checking');
      setError(null);

      try {
        const userDoc = await getDoc(doc(firestore, 'user', firebaseUser.uid));
        const data = userDoc.data();
        if (!userDoc.exists() || data?.rtMonitorUser !== true) {
          setError('Usuário sem permissão para acessar o monitor.');
          setUser(null);
          setStatus('unauthenticated');
          await signOut(auth);
          return;
        }
        setUser(firebaseUser);
        setStatus('authenticated');
      } catch (err) {
        console.error('Erro ao validar permissões do monitor', err);
        setError('Falha ao validar permissões. Tente novamente.');
        setUser(null);
        setStatus('unauthenticated');
        await signOut(auth);
      }
    });

    return unsubscribe;
  }, [bypassAuth]);

  const login = useCallback(
    async (email: string, password: string) => {
      if (bypassAuth) {
        setStatus('bypassed');
        return;
      }
      if (!auth) {
        throw new Error('Firebase Auth não inicializado.');
      }
      setError(null);
      setStatus('checking');
      await signInWithEmailAndPassword(auth, email, password).catch((err) => {
        console.error('Erro no login do monitor', err);
        setError('Credenciais inválidas.');
        setStatus('unauthenticated');
        throw err;
      });
    },
    [bypassAuth]
  );

  const logout = useCallback(async () => {
    if (bypassAuth) {
      setStatus('bypassed');
      return;
    }
    if (!auth) {
      throw new Error('Firebase Auth não inicializado.');
    }
    await signOut(auth);
    setUser(null);
    setStatus('unauthenticated');
  }, [bypassAuth]);

  const value = useMemo(
    () => ({
      status,
      user,
      error,
      login,
      logout,
    }),
    [status, user, error, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuthContext deve ser usado dentro de AuthProvider');
  }
  return ctx;
};

